import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Provider } from 'react-redux';
import { SnackbarProvider } from 'notistack';
import store from './store';
import { setApiStore } from './api/axios';
import { LanguageProvider } from './context/LanguageContext';
import Home from './Pages/Home';
import Actualite from './Pages/Actualite';
import Layout from './components/Layout';
import './global.css';
import ProtectedRoute from './components/Dashboard/ProtectedRoute';
import DashboardLayout from './components/Dashboard/DashboardLayout';
import Login from './Pages/Dashboard/Login';
import ChangePassword from './Pages/Dashboard/ChangePassword';
import DashboardHome from './Pages/Dashboard/Home';
import Users from './Pages/Dashboard/Users';
import PendingPosts from './Pages/Dashboard/PendingPosts';
import PostForm from './Pages/Dashboard/PostForm';
import Notifications from './Pages/Dashboard/Notifications';
import AlumniData from './Pages/Dashboard/AlumniData';
import NewsletterData from './Pages/Dashboard/NewsletterData';
import SendNewsletter from './Pages/Dashboard/SendNewsletter';
import SentHistory from './Pages/Dashboard/SentHistory';
import AnnouncementEventList from './Pages/Dashboard/AnnouncementEventList';
import AnnouncementEventForm from './Pages/Dashboard/AnnouncementEventForm';
import PartnershipList from './Pages/Dashboard/PartnershipList';
import PartnershipForm from './Pages/Dashboard/PartnershipForm';
import Historique from './Pages/QuiSommesNous/Historique';
import MissionVision from './Pages/QuiSommesNous/MissionVision';
import Valeurs from './Pages/QuiSommesNous/Valeurs';
import Campus from './Pages/QuiSommesNous/Campus';
import Partenariats from './Pages/QuiSommesNous/Partenariats';
import DroitEcoGestion from './Pages/Formation/Facultes/DroitEcoGestion';
import LettresArts from './Pages/Formation/Facultes/LettresArts';
import Medecine from './Pages/Formation/Facultes/Medecine';
import SciencesAgro from './Pages/Formation/Facultes/SciencesAgro';
import SciencesTech from './Pages/Formation/Facultes/SciencesTech';
import Iscoba from './Pages/Formation/Instituts/Iscoba';
import Issav from './Pages/Formation/Instituts/Issav';
import Issi from './Pages/Formation/Instituts/Issi';
import Ipl from './Pages/Formation/Instituts/Ipl';
import Iuss from './Pages/Formation/Instituts/Iuss';
import Nutrition from './Pages/Formation/Masters/Nutrition';
import DevRural from './Pages/Formation/Masters/DevRural';
import Agribusiness from './Pages/Formation/Masters/Agribusiness';
import DroitPublic from './Pages/Formation/Masters/DroitPublic';
import DroitPrive from './Pages/Formation/Masters/DroitPrive';
import PolitiquesEconomiques from './Pages/Formation/Masters/PolitiquesEconomiques';
import ManagementProjets from './Pages/Formation/Masters/ManagementProjets';
import BanqueMicrofinance from './Pages/Formation/Masters/BanqueMicrofinance';
import CentreRecherche from './Pages/Recherche/CentreRecherche';
import Publications from './Pages/Recherche/Publications';
import Calendrier from './Pages/Etudiants/Calendrier';
import Admission from './Pages/Etudiants/Admission';
import AmericanCorner from './Pages/Etudiants/AmericanCorner';
import IdeaxBox from './Pages/Etudiants/IdeaxBox';
import Blog from './Pages/Etudiants/Blog';
import BlogDetail from './Pages/BlogDetail';
import ArticleDetail from './Pages/ArticleDetail';
import AuthorProfile from './Pages/AuthorProfile';
import AuthorList from './Pages/AuthorList';
import EtudiantsInternationaux from './Pages/EtudiantsInternationaux';
import EducationPaix from './Pages/EducationPaix';
import Evenements from './Pages/Alumni/Evenements';
import Direction from './Pages/Administration/Direction';
import General from './Pages/Administration/General';
import RH from './Pages/Administration/RH';
import Finance from './Pages/Administration/Finance';
import Galerie from './Pages/Galerie';
import ScrollToTop from './components/ScrollToTop';
import AnitaAI from './components/AnitaAI';
import { HelmetProvider } from 'react-helmet-async';
import { lazy, Suspense } from 'react';
import { Box, CircularProgress } from '@mui/material';


setApiStore(store);

const Settings = lazy(() => import('./Pages/Dashboard/Settings'));
const Posts = lazy(() => import('./Pages/Dashboard/Posts'));

function App() {
  return (
    <Provider store={store}>
      <LanguageProvider>
      <SnackbarProvider
        maxSnack={3}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        autoHideDuration={4000}
      >
        <HelmetProvider>
          <Router>
            <ScrollToTop />
            <Routes>
              {/* Dashboard auth */}
              <Route path="/login" element={<Login />} />
              <Route
                path="/dashboard/change-password"
                element={
                  <ProtectedRoute>
                    <ChangePassword />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <DashboardLayout />
                  </ProtectedRoute>
                }
              >
                <Route index element={<DashboardHome />} />
                <Route path="users" element={<Users />} />
                <Route path="posts" element={<Suspense fallback={<Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 8, gap: 2 }}><CircularProgress size={44} thickness={4} /><Box component="span" sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>Chargement…</Box></Box>}><Posts /></Suspense>} />
                <Route path="posts/new" element={<PostForm />} />
                <Route path="posts/edit/:id" element={<PostForm />} />
                <Route path="pending" element={<PendingPosts />} />
                <Route path="notifications" element={<Notifications />} />
                <Route path="alumni" element={<AlumniData />} />
                <Route path="alumni/send" element={<SendNewsletter />} />
                <Route path="alumni/history" element={<SentHistory />} />
                <Route path="newsletters" element={<NewsletterData />} />
                <Route path="announcement-events" element={<AnnouncementEventList />} />
                <Route path="announcement-events/new" element={<AnnouncementEventForm />} />
                <Route path="announcement-events/edit/:id" element={<AnnouncementEventForm />} />
                <Route path="partnerships" element={<PartnershipList />} />
                <Route path="partnerships/new" element={<PartnershipForm />} />
                <Route path="partnerships/edit/:id" element={<PartnershipForm />} />
                <Route path="settings" element={<Suspense fallback={<Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>}><Settings /></Suspense>} />
              </Route>

              {/* Public site */}
              <Route element={<Layout />}>
                <Route path="/" element={<Home />} />
                <Route path="/actualites" element={<Actualite />} />
                <Route path="/historique" element={<Historique />} />
                <Route path="/mission" element={<MissionVision />} />
                <Route path="/vision" element={<Navigate to="/mission" replace />} />
                <Route path="/valeurs" element={<Valeurs />} />
                <Route path="/campus" element={<Campus />} />
                <Route path="/partenariats" element={<Partenariats />} />
                <Route path="/faculte/fdseg" element={<DroitEcoGestion />} />
                <Route path="/faculte/fllca" element={<LettresArts />} />
                <Route path="/faculte/medecine" element={<Medecine />} />
                <Route path="/faculte/sav" element={<SciencesAgro />} />
                <Route path="/faculte/sciences" element={<SciencesTech />} />
                <Route path="/institut/iscoba" element={<Iscoba />} />
                <Route path="/institut/issav" element={<Issav />} />
                <Route path="/institut/issi" element={<Issi />} />
                <Route path="/institut/ipl" element={<Ipl />} />
                <Route path="/institut/iuss" element={<Iuss />} />
                <Route path="/masters/nutrition" element={<Nutrition />} />
                <Route path="/masters/developpement-rural" element={<DevRural />} />
                <Route path="/masters/agribusiness" element={<Agribusiness />} />
                <Route path="/masters/droit-public" element={<DroitPublic />} />
                <Route path="/masters/droit-prive" element={<DroitPrive />} />
                <Route path="/masters/politiques-economiques" element={<PolitiquesEconomiques />} />
                <Route path="/masters/management-projets" element={<ManagementProjets />} />
                <Route path="/masters/banque-microfinance" element={<BanqueMicrofinance />} />
                <Route path="/recherche/centre" element={<CentreRecherche />} />
                <Route path="/recherche/publications" element={<Publications />} />
                <Route path="/etudiants/calendrier" element={<Calendrier />} />
                <Route path="/admission" element={<Admission />} />
                <Route path="/etudiants-internationaux" element={<EtudiantsInternationaux />} />
                <Route path="/etudiants/american-corner" element={<AmericanCorner />} />
                <Route path="/etudiants/ideax-box" element={<IdeaxBox />} />
                <Route path="/etudiants/blog" element={<Blog />} />
                <Route path="/article/:id" element={<ArticleDetail />} />
                <Route path="/auteurs" element={<AuthorList />} />
                <Route path="/auteur/:id" element={<AuthorProfile />} />
                <Route path="/blog/:id" element={<BlogDetail />} />
                <Route path="/education-paix" element={<EducationPaix />} />
                <Route path="/galerie" element={<Galerie />} />
                <Route path="/alumni/events" element={<Evenements />} />
                <Route path="/administration/direction" element={<Direction />} />
                <Route path="/administration/general" element={<General />} />
                <Route path="/administration/hr" element={<RH />} />
                <Route path="/administration/finance" element={<Finance />} />
              </Route>

              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            <AnitaAI />
          </Router>
        </HelmetProvider>
      </SnackbarProvider>
      </LanguageProvider>
    </Provider>
  );
}

export default App;
